import { useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FadeIn, Eyebrow } from "./primitives";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long" });
  } catch {
    return "";
  }
};

export const Letters = ({ letters, onLetterSent }) => {
  const [name, setName] = useState("");
  const [question, setQuestion] = useState("");
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || question.trim().length < 5) {
      toast.error("Leave your name and a question of at least a few words.");
      return;
    }
    setSending(true);
    try {
      await api.post("/letters", { name: name.trim(), question: question.trim() });
      toast.success("Your letter is on its way to the show.");
      setName("");
      setQuestion("");
      onLetterSent?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send your letter. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="letters" data-testid="letters-section" className="border-t border-bone/10 bg-coal">
      <div className="mx-auto max-w-6xl px-6 py-24">
        <FadeIn>
          <Eyebrow>Letters to the show</Eyebrow>
          <h2 className="mt-6 max-w-2xl font-display text-3xl font-light tracking-tight text-bone sm:text-4xl">
            Ask the question cinema never answered for you.
          </h2>
          <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-zinc-400">
            Leave a question or a story. The honest ones have a way of becoming episodes.
          </p>
        </FadeIn>

        <div className="mt-16 grid gap-16 md:grid-cols-2">
          <FadeIn delay={0.1}>
            <form onSubmit={handleSubmit} data-testid="letter-form" className="space-y-6">
              <div>
                <label htmlFor="letter-name" className="font-mono text-xs uppercase tracking-[0.2em] text-smoke">
                  Your name
                </label>
                <input
                  id="letter-name"
                  data-testid="letter-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={80}
                  placeholder="How should we call you?"
                  className="mt-3 w-full border border-bone/15 bg-noir px-4 py-3 text-base font-light text-bone placeholder:text-zinc-600 focus:border-ruby focus:outline-none focus:ring-1 focus:ring-ruby"
                />
              </div>
              <div>
                <label htmlFor="letter-question" className="font-mono text-xs uppercase tracking-[0.2em] text-smoke">
                  Your question
                </label>
                <textarea
                  id="letter-question"
                  data-testid="letter-question-input"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  maxLength={600}
                  rows={5}
                  placeholder="Is it real — or did a film teach me to expect it?"
                  className="mt-3 w-full resize-none border border-bone/15 bg-noir px-4 py-3 text-base font-light text-bone placeholder:text-zinc-600 focus:border-ruby focus:outline-none focus:ring-1 focus:ring-ruby"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="letter-submit-button"
                className="flex items-center gap-3 bg-ruby px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-ruby-bright disabled:opacity-50"
              >
                <Send size={14} /> {sending ? "Sending…" : "Send your letter"}
              </button>
            </form>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div data-testid="recent-letters" className="space-y-px border border-bone/10 bg-bone/10">
              <p className="bg-noir px-6 py-4 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
                Recently received
              </p>
              {letters.length === 0 && (
                <p className="bg-noir px-6 py-8 text-sm font-light text-zinc-500">
                  The mailbox is open. Yours could be the first.
                </p>
              )}
              {letters.map((letter) => (
                <blockquote
                  key={letter.id}
                  data-testid={`letter-card-${letter.id}`}
                  className="bg-noir px-6 py-5 transition-colors duration-300 hover:bg-ash"
                >
                  <p className="font-display text-base italic leading-relaxed text-bone">
                    “{letter.question}”
                  </p>
                  <footer className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                    — {letter.name} · {formatDate(letter.created_at)}
                  </footer>
                </blockquote>
              ))}
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};
